import {createDialogOverlay, fillDialog, pvpButton, markDigitizationUI} from "../DialogHelpers.js";
import {hostDialogAvailable, requestHostConfirm, htmlMessageToText} from "../HostDialog.js";

import {t, T, localized, numberLocale} from "../i18n/index.js";
import {QUICK_CLASSIFICATIONS} from "../../viewer/ClassificationQuickToggles.js";
// Result popup for "Areal og volumen" (task section 12/13) - same dark-
// modal convention as every other Digitalisering prompt (WallThicknessPrompt.js/
// DistancePrompt.js/VectorEditingMenu.js's showGeometryWarning/HelpPanel.js's
// showHelpTopicPopup) - one small, self-contained popup per file is this
// codebase's established pattern for these.

function formatNumber(value, decimals){
	return value.toLocaleString(numberLocale(), {minimumFractionDigits: decimals, maximumFractionDigits: decimals});
}

function formatSigned(value, decimals, unit){
	const sign = value > 0 ? "+" : (value < 0 ? "" : "±"); // toLocaleString already renders the minus sign for negatives
	return `${sign}${formatNumber(value, decimals)} ${unit}`;
}

function qaRow(label, value){
	return `<div style="display:flex; justify-content:space-between; gap:12px; padding:1px 0;"><span style="opacity:0.75;">${label}</span><span>${value}</span></div>`;
}

// The compact, primary result - task section 3/10/12 of the UI-
// simplification iteration: "Areal", "Påfyldning", "Udgravning" only (no
// Netto/Volumenbevægelse/Dækning/QA) - used for BOTH the 3D result label
// (FeatureVisual.setResultLabel(), joined with "\n") and reused verbatim as
// the text content of Egenskaber's primary summary (buildVolumeSummaryHtml
// below), so the two never drift apart.
export function buildVolumeLabelLines(result){
	const lines = [
		t("Areal: {0} m²", formatNumber(result.referenceArea, 2)),
		t("Påfyldning: {0}", formatSigned(result.positive, 3, "m³")),
		t("Udgravning: {0}", formatSigned(result.negative, 3, "m³")),
	];
	if(hasExcludedClasses(result)) lines.push(t("Uden klasser: {0}", excludedClassNames(result)));
	return lines;
}

function hasExcludedClasses(result){
	return !!result.excludedClasses && result.excludedClasses.length > 0;
}

// The classes that actually lost points in this measurement, by the names the quick toggles use, else by code.
function excludedClassNames(result){
	return result.excludedClasses.map((code) => {
		const known = QUICK_CLASSIFICATIONS.find((entry) => entry.code === code);
		return known ? known.label : t("klasse {0}", code);
	}).join(", ");
}

// Egenskaber's primary (always-visible) summary - task section 9: "Vis
// minimum: Areal, Påfyldning, Udgravning", plus Dækning (explicitly
// permitted - "fortæller noget relevant om hvor meget af polygonen der
// faktisk indgår i beregningen"). Everything else (Referenceareal vs.
// Datadækket areal distinction, Overfladeareal, Netto, Volumenbevægelse,
// full QA) stays exactly where it already was - the separate, still-
// collapsed-by-default "Teknisk QA" section (buildTechnicalQaHtml) - not
// expanded or duplicated here.
export function buildVolumeSummaryHtml(result){
	const p = result;
	const emptyWarning = p.noObservationsInsidePolygon
		? `<div style="margin-bottom:8px; padding:6px 8px; background:#4a3a12; border:1px solid #7a5c1a; border-radius:3px; font-size:12px;">${t("⚠ Ingen faktiske punktsky-observationer fundet inden for polygonen - resultatet viser derfor intet datadækket areal.")}</div>`
		: "";
	return `
		<div style="font-size:13px; line-height:1.6;">
			${emptyWarning}
			${qaRow(t("Areal:"), formatNumber(p.referenceArea, 2) + " m²")}
			${qaRow(t("Påfyldning:"), formatSigned(p.positive, 3, "m³"))}
			${qaRow(t("Udgravning:"), formatSigned(p.negative, 3, "m³"))}
			${qaRow(t("Dækning:"), formatNumber(p.coverageRatio * 100, 2) + " %")}
			${hasExcludedClasses(p) ? qaRow(t("Uden klasser:"), excludedClassNames(p)) : ""}
		</div>
	`;
}

export function buildVolumeResultHtml(result){
	const p = result;
	const emptyWarning = p.noObservationsInsidePolygon
		? `<div style="margin-bottom:8px; padding:6px 8px; background:#4a3a12; border:1px solid #7a5c1a; border-radius:3px; font-size:12px;">${t("⚠ Ingen faktiske punktsky-observationer fundet inden for polygonen - resultatet viser derfor intet datadækket areal.")}</div>`
		: "";
	return `
		<div style="font-size:13px; line-height:1.6;">
			<div style="font-weight:bold; margin-bottom:6px;">${t("Mål areal og volumen")}</div>
			${emptyWarning}
			${qaRow(t("Referenceareal:"), formatNumber(p.referenceArea, 2) + " m²")}
			${qaRow(t("Datadækket areal:"), formatNumber(p.coveredArea, 2) + " m²")}
			${qaRow(t("Dækning:"), formatNumber(p.coverageRatio * 100, 2) + " %")}
			${qaRow(t("Overfladeareal:"), formatNumber(p.surfaceArea, 2) + " m²")}
			<div style="height:8px;"></div>
			${qaRow(t("Påfyldning:"), formatSigned(p.positive, 3, "m³"))}
			${qaRow(t("Udgravning:"), formatSigned(p.negative, 3, "m³"))}
			${qaRow(t("Netto:"), formatSigned(p.net, 3, "m³"))}
			${qaRow(t("Volumenbevægelse:"), formatNumber(p.movement, 3) + " m³")}
		</div>
	`;
}

function formatMs(ms){ return `${formatNumber(ms, 0)} ms`; }

export function buildTechnicalQaHtml(result){
	const p = result;
	const conv = p.convergence;
	const convergenceRows = conv
		? `
			${qaRow(t("Volumen (coarse):"), `V+ ${formatNumber(conv.coarse.positive, 3)} / V- ${formatNumber(conv.coarse.negative, 3)}`)}
			${qaRow(t("Volumen (normal):"), `V+ ${formatNumber(conv.normal.positive, 3)} / V- ${formatNumber(conv.normal.negative, 3)}`)}
			${qaRow(t("Volumen (fine):"), `V+ ${formatNumber(conv.fine.positive, 3)} / V- ${formatNumber(conv.fine.negative, 3)}`)}
			${qaRow(t("Positiv konvergens (normal→fine):"), formatNumber(conv.deltaPositiveNormalFine, 4) + " m³")}
			${qaRow(t("Negativ konvergens (normal→fine):"), formatNumber(conv.deltaNegativeNormalFine, 4) + " m³")}
		`
		: qaRow(t("Konvergens:"), t("ikke beregnet (kun i standardflow)"));
	const roiRows = (p.roiNodesLoaded !== undefined)
		? `
			<div style="height:6px;"></div>
			${qaRow("ROI-frontier level:", formatNumber(p.roiFrontierLevel || 0, 0))}
			${qaRow("ROI point budget:", formatNumber(p.roiPointBudget || 0, 0))}
			${qaRow(t("Noder indlæst:"), formatNumber(p.roiNodesLoaded, 0))}
			${qaRow(t("Punkter i indlæste noder:"), formatNumber(p.roiNodePointCount || 0, 0))}
			${qaRow(t("Punkter efter bbox-filter:"), formatNumber(p.roiBboxFilteredPointCount || 0, 0))}
		`
		: "";
	// task section 12/21: measurement-TIN vs visual-TIN triangle counts, so
	// the QA rendering itself can be diagnosed - `visualTriangleCount`/
	// `qaMeshCount`/`qaBuildTimeMs` are only present once VolumeQaVisual.js
	// has actually built (best-effort - see volumeTool.js's own try/catch).
	const qaDebug = p.qaDebug;
	const qaVizRows = (p.visualTriangleCount !== undefined)
		? `
			<div style="height:6px;"></div>
			${qaRow(t("Measurement-trekanter (understøttede):"), formatNumber(p.measurementTriangleCount || 0, 0))}
			${qaRow(t("Visual-trekanter (QA, fuld, ikke capped):"), formatNumber(p.visualTriangleCount || 0, 0))}
			${qaRow(t("QA mesh-antal:"), formatNumber(p.qaMeshCount || 0, 0))}
			${qaRow("QA build-tid:", formatMs(p.qaBuildTimeMs || 0))}
			${qaDebug ? qaRow("Visual coordinate mode:", qaDebug.coordinateMode) : ""}
			${qaDebug ? qaRow(t("Maks. |lokal position|:"), formatNumber(qaDebug.maxAbsLocalPosition, 3) + " m") : ""}
			${qaDebug ? qaRow("Frame origin:", `X ${formatNumber(qaDebug.frameOrigin.x, 2)}, Y ${formatNumber(qaDebug.frameOrigin.y, 2)}, Z ${formatNumber(qaDebug.frameOrigin.z, 2)}`) : ""}
		`
		: "";
	return `
		<div style="font-size:12px; line-height:1.55; opacity:0.9;">
			${qaRow("Raw ROI points:", formatNumber(p.rawPointCount, 0))}
			${qaRow(t("Punkter inden for polygon:"), formatNumber(p.interiorPointCount != null ? p.interiorPointCount : p.rawPointCount, 0))}
			${qaRow(t("Reducerede observationer:"), formatNumber(p.observationCount, 0))}
			${qaRow(t("Randpunkter (original → densificeret):"), `${formatNumber((p.plane && p.boundaryLocal ? p.boundaryLocal.length : 0), 0)} → ${formatNumber(p.densifiedBoundaryVertexCount || 0, 0)}`)}
			${qaRow("Triangle count:", formatNumber(p.triangleCount, 0))}
			${qaRow("Supported triangle count:", formatNumber(p.supportedTriangleCount, 0))}
			${qaRow("Excluded ambiguous cells:", formatNumber(p.excludedAmbiguousCells, 0))}
			${qaRow(t("Referenceplan RMS:"), formatNumber(p.plane.residualRms, 4) + " m")}
			${qaRow(t("Referenceplan maks. residual:"), formatNumber(p.plane.residualMax, 4) + " m")}
			${qaRow(t("Referenceplan hældning:"), formatNumber(p.plane.inclinationDeg, 1) + " °")}
			${qaRow("Coverage:", formatNumber(p.coverageRatio * 100, 2) + " %")}
			<div style="height:6px;"></div>
			${convergenceRows}
			${roiRows}
			${qaVizRows}
			<div style="height:6px;"></div>
			${qaRow("ROI load-tid:", formatMs(p.timings.roiLoad || 0))}
			${qaRow("Filter-tid:", formatMs(p.timings.filter || 0))}
			${qaRow("Spacing-tid:", formatMs(p.timings.spacing || 0))}
			${qaRow("Reduction-tid:", formatMs(p.timings.reduction || 0))}
			${qaRow("Triangulation-tid:", formatMs(p.timings.triangulation || 0))}
			${qaRow("Integration-tid:", formatMs(p.timings.integration || 0))}
			${qaRow(t("Total beregningstid:"), formatMs(p.timings.total))}
		</div>
	`;
}

export function showVolumeResultPanel(result){
	const overlay = document.createElement("div");
	overlay.style.cssText = [
		"position: fixed", "inset: 0", "background: rgba(0,0,0,0.45)", "z-index: 10002",
		"display: flex", "align-items: center", "justify-content: center", "font-family: Arial, sans-serif",
	].join(";");

	const box = document.createElement("div");
	box.style.cssText = [
		"background: #2b2b2b", "color: #fff", "border-radius: 6px", "padding: 16px 18px",
		"width: 420px", "max-width: calc(100vw - 40px)", "max-height: calc(100vh - 80px)", "overflow-y: auto",
		"box-shadow: 0 4px 18px rgba(0,0,0,0.5)",
	].join(";");

	const resultEl = document.createElement("div");
	resultEl.innerHTML = buildVolumeResultHtml(result);

	const qaToggle = document.createElement("div");
	qaToggle.textContent = t("▸ Teknisk QA");
	qaToggle.style.cssText = "margin-top:12px; cursor:pointer; font-size:12px; opacity:0.8; user-select:none;";

	const qaBody = document.createElement("div");
	qaBody.innerHTML = buildTechnicalQaHtml(result);
	qaBody.style.display = "none";
	qaBody.style.marginTop = "6px";

	let qaOpen = false;
	qaToggle.addEventListener("click", () => {
		qaOpen = !qaOpen;
		qaBody.style.display = qaOpen ? "block" : "none";
		qaToggle.textContent = (qaOpen ? "▾" : "▸") + t(" Teknisk QA");
	});

	const closeRow = document.createElement("div");
	closeRow.style.cssText = "display:flex; justify-content:flex-end; margin-top:12px;";
	const closeButton = document.createElement("button");
	closeButton.textContent = t("Luk");
	closeButton.style.cssText = "padding:5px 14px; border-radius:3px; border:none; background:#3a3a3a; color:#fff; cursor:pointer;";
	closeRow.appendChild(closeButton);

	box.appendChild(resultEl);
	box.appendChild(qaToggle);
	box.appendChild(qaBody);
	box.appendChild(closeRow);
	overlay.appendChild(box);
	markDigitizationUI(overlay);
	document.body.appendChild(overlay);

	const close = () => document.body.contains(overlay) && document.body.removeChild(overlay);
	closeButton.addEventListener("click", close);
	overlay.addEventListener("mousedown", (e) => { if(e.target === overlay) close(); });
	box.setAttribute("tabindex", "-1");
	box.addEventListener("keydown", (e) => { if(e.key === "Escape"){ e.stopPropagation(); close(); } });
	box.focus();
}

const TECHNICAL_LABELS = localized({
	rawPointCount: T("Rå punkter"),
	interiorPointCount: T("Punkter i polygon"),
	observationCount: T("Reducerede punkter"),
	boundaryVertexCount: T("Randpunkter"),
	triangleCount: T("Trekanter"),
	stage: T("Aktuelt trin"),
	limit: T("Grænse"),
});

function buildTechnicalDetailsHtml(technical){
	if(!technical) return "";
	const rows = Object.keys(TECHNICAL_LABELS)
		.filter((key) => technical[key] !== undefined)
		.map((key) => qaRow(TECHNICAL_LABELS[key] + ":", typeof technical[key] === "number" ? formatNumber(technical[key], 0) : technical[key]))
		.join("");
	if(!rows) return "";
	return `<div style="margin-top:10px; padding:8px; background:#00000022; border-radius:3px; font-size:12px;">${rows}</div>`;
}

// Teknisk detalje som ren tekst (til host-beskeden): samme rækker som buildTechnicalDetailsHtml().
function buildTechnicalDetailsText(technical){
	if(!technical) return "";
	return Object.keys(TECHNICAL_LABELS)
		.filter((key) => technical[key] !== undefined)
		.map((key) => `${TECHNICAL_LABELS[key]}: ${typeof technical[key] === "number" ? formatNumber(technical[key], 0) : technical[key]}`)
		.join("\n");
}

// dm (valgfri): findes en vært (viewer.pvUi), vises fejlen som almindelig host-besked (centreret, Luk); den tekniske detalje
// bevares som sekundært tekstafsnit. Uden vært/dm: DOM-dialogen herunder (standalone).
export function showVolumeErrorPanel(message, technical, dm){
	if(dm && hostDialogAvailable(dm.viewer)){
		const details = buildTechnicalDetailsText(technical);
		requestHostConfirm(dm.viewer, {kind: "message", size: "s", title: t("Mål areal og volumen"), text: htmlMessageToText(message) + (details ? `\n\n${details}` : "")});
		return;
	}
	const {overlay, box} = createDialogOverlay(dm, 340, {center: true});
	fillDialog(box, {
		title: t("Mål areal og volumen"),
		body: `<p class="pvp-desc" style="color:inherit">${message}</p>
			${buildTechnicalDetailsHtml(technical)}`,
		actions: pvpButton('ok', t("Luk"), 'primary'),
	});
	const close = () => overlay.isConnected && document.body.removeChild(overlay);
	box.querySelector('[data-action="ok"]').addEventListener("click", close);
	box.setAttribute("tabindex", "-1");
	box.addEventListener("keydown", (e) => { if(e.key === "Escape"){ e.stopPropagation(); close(); } });
	box.focus();
}
